import {
  BookOpen,
  Check,
  Cog,
  Package,
  Paperclip,
  Pause,
  Play,
  Plus,
  SendHorizontal,
  Square,
  Workflow,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import { ChatAttachments } from '@/components/agents/chat-attachments';
import { Modal } from '@/components/modal';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Textarea } from '@/components/ui/textarea';
import { ROUTES } from '@/constants/routes';
import { useSkillList } from '@/hooks/queries/skills/use-skill-list';
import { useWorkflowList } from '@/hooks/queries/workflow/use-workflow-list';
import { useOrgNavigate } from '@/hooks/use-org-navigate';
import { cn } from '@/lib/utils';

import { useAgentChatContext } from './agent-chat-provider';
import { useClarificationPauseDetection } from '@/hooks/use-clarification-pause-detection';
import { pauseClarificationCountdown } from '@/services/workflow/pause-clarification-countdown';
import { resumeClarificationCountdown } from '@/services/workflow/resume-clarification-countdown';
import type { RelayedRunEvent } from './run-event';
import { ChatSettingsContent, useChatSettingsActive } from './chat-settings';
import {
  filterWorkflowOptions,
  toWorkflowOptions,
  workflowRunMessage,
  type WorkflowCommandOption,
} from './helpers/workflow-slash';
import { useChatAttachmentUploads } from './use-chat-attachment-uploads';
import { WorkflowSlashMenu } from './workflow-slash-menu';
import { useComposerAutosize } from './use-composer-autosize';

interface PromptBarProps {
  placeholder?: string;
  attachmentsEnabled: boolean;
  customInstructionsEnabled?: boolean;
  clarificationSettingsEnabled?: boolean;
  skillsEnabled?: boolean;
  /** Offers "/" workflow commands and the workflow "+" menu items. */
  workflowCommands?: boolean;
}

function waitingPlaceholder(
  clarification: RelayedRunEvent | null,
  review: RelayedRunEvent | null,
): string | undefined {
  if (clarification) {
    return `Reply to answer “${clarification.nodeTitle ?? 'the node'}”, or ask me anything`;
  }
  if (review) {
    return `Tell me what to change in “${review.nodeTitle ?? 'the node'}”'s draft, or say it looks good`;
  }
  return undefined;
}

export function PromptBar({
  placeholder,
  attachmentsEnabled,
  customInstructionsEnabled = false,
  clarificationSettingsEnabled = false,
  skillsEnabled = false,
  workflowCommands = false,
}: PromptBarProps) {
  const {
    orgId,
    agentId,
    conversationId,
    messages,
    isSending,
    isPaused,
    sendMessage,
    stopTurn,
    pauseTurn,
    resumeTurn,
    composerRef,
    registerComposerDraftSetter,
    customInstructions,
    setCustomInstructions,
    armedSkill,
    setArmedSkill,
    promptQueue,
    pendingClarification,
    pendingReview,
    setComposerDrafting,
  } = useAgentChatContext();

  const [input, setInput] = useState('');
  const [attachedWorkflow, setAttachedWorkflow] =
    useState<WorkflowCommandOption | null>(null);
  const [slashDismissed, setSlashDismissed] = useState(false);
  const [activeOptionIndex, setActiveOptionIndex] = useState(0);
  const [plusMenuOpen, setPlusMenuOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Armed by a menu item, run once the menu finishes closing
  // (onCloseAutoFocus). The instructions popover opens only after the menu
  // released focus -- any earlier and its own focus-outside handling
  // dismisses it -- and a composer focus has to outlive the menu's own
  // focus restore to the "+" button.
  const afterMenuCloseRef = useRef<(() => void) | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useOrgNavigate();
  const {
    attachments,
    addFiles,
    removeAttachment,
    clearAttachments,
    uploadedAttachments,
    isUploading,
  } = useChatAttachmentUploads(orgId, agentId);

  const handleInputChange = (value: string) => {
    setInput(value);
    setSlashDismissed(false);
    setActiveOptionIndex(0);
  };

  useEffect(
    // handleInputChange only closes over stable state setters, so the
    // initial closure registered here never goes stale.
    () => registerComposerDraftSetter(handleInputChange),
    [registerComposerDraftSetter],
  );

  useComposerAutosize(composerRef, input);

  const { data: workflowList, isLoading: isLoadingWorkflows } =
    useWorkflowList(workflowCommands);
  const workflows = workflowList?.workflows;
  const isWorkflowAdmin = workflowList?.isWorkflowAdmin ?? false;

  // Fetched when the "+" menu first opens so the submenu is ready by the
  // time it's hovered.
  const {
    data: skillsData,
    isLoading: isLoadingSkills,
    isError: skillsErrored,
  } = useSkillList(skillsEnabled && plusMenuOpen);
  const skillOptions = skillsData?.skills ?? [];

  const slashActive =
    workflowCommands &&
    !attachedWorkflow &&
    input.startsWith('/') &&
    !slashDismissed;
  const slashQuery = slashActive ? input.slice(1) : '';
  const menuOptions = useMemo(
    () =>
      slashActive
        ? filterWorkflowOptions(
            toWorkflowOptions(workflows ?? [], { isWorkflowAdmin }),
            slashQuery,
          )
        : [],
    [slashActive, workflows, slashQuery, isWorkflowAdmin],
  );
  const menuOpen = slashActive;

  const selectWorkflow = (option: WorkflowCommandOption) => {
    setAttachedWorkflow(option);
    setInput('');
    setActiveOptionIndex(0);
    composerRef.current?.focus();
  };

  const { getRootProps, isDragActive } = useDropzone({
    onDrop: addFiles,
    noClick: true,
    noKeyboard: true,
    multiple: true,
    // Disabled mid-turn too: a queued prompt is text-only (see handleSend),
    // so a drop landing then would have nothing to ride out on.
    disabled: !attachmentsEnabled || isSending,
  });

  const handleSend = async () => {
    const trimmed = input.trim();
    // While a reply is still streaming the send action queues instead:
    // the prompt joins the strip above the composer and dispatches, in
    // order, as turns complete. Text (and a slash-attached workflow)
    // only -- attachment controls are disabled mid-turn, and any files
    // staged before the turn stay staged for the next manual send.
    if (isSending) {
      if (menuOpen || (!trimmed && !attachedWorkflow)) return;
      promptQueue.enqueue(
        attachedWorkflow
          ? workflowRunMessage(attachedWorkflow, trimmed)
          : trimmed,
      );
      setAttachedWorkflow(null);
      setInput('');
      composerRef.current?.focus();
      return;
    }
    if (
      menuOpen ||
      (!trimmed && uploadedAttachments.length === 0 && !attachedWorkflow) ||
      isUploading ||
      isSending
    ) {
      return;
    }
    const text = attachedWorkflow
      ? workflowRunMessage(attachedWorkflow, trimmed)
      : trimmed;
    const files = uploadedAttachments.map((a) => ({
      fileId: a.fileId,
      filename: a.filename,
    }));
    clearAttachments();
    setAttachedWorkflow(null);
    setInput('');
    // The answer is on its way; the operator's turn holds the countdown now.
    pauseDetection.reset();
    await sendMessage(text, files);
    composerRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (menuOpen) {
      if (menuOptions.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setActiveOptionIndex((i) => (i + 1) % menuOptions.length);
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setActiveOptionIndex(
            (i) => (i - 1 + menuOptions.length) % menuOptions.length,
          );
          return;
        }
        if ((e.key === 'Enter' && !e.shiftKey) || e.key === 'Tab') {
          e.preventDefault();
          selectWorkflow(menuOptions[activeOptionIndex] ?? menuOptions[0]);
          return;
        }
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setSlashDismissed(true);
        return;
      }
      // Nothing to pick yet (loading, no workflows, no match): the query is
      // still a picker session, so Enter waits rather than posting "/foo".
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        return;
      }
    }
    if (
      e.key === 'Backspace' &&
      input.length === 0 &&
      attachedWorkflow !== null
    ) {
      e.preventDefault();
      setAttachedWorkflow(null);
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const settingsEnabled =
    customInstructionsEnabled || clarificationSettingsEnabled;
  const settingsActive = useChatSettingsActive(
    customInstructions,
    clarificationSettingsEnabled,
  );

  const conversationActive = conversationId !== null || messages.length > 0;

  // A bounded question's countdown holds while an answer is being typed,
  // and runs again after a pause in typing with the box empty; the same
  // detector the canvas card uses. The operator's turn holds it again while
  // it delivers the answer.
  const countdownTarget =
    pendingClarification?.deadlineEpochMs &&
    pendingClarification.workflowId &&
    pendingClarification.sessionId &&
    orgId
      ? {
          workflowId: pendingClarification.workflowId,
          sessionId: pendingClarification.sessionId,
          orgId,
        }
      : null;
  const countdownTargetRef = useRef(countdownTarget);
  countdownTargetRef.current = countdownTarget;
  const signalCountdown = useCallback(
    async (
      signal: (
        workflowId: string,
        sessionId: string,
        orgId: string,
      ) => Promise<unknown>,
    ) => {
      const target = countdownTargetRef.current;
      if (!target) return;
      await signal(target.workflowId, target.sessionId, target.orgId);
    },
    [],
  );
  const pauseDetection = useClarificationPauseDetection({
    isClarificationActive: !!countdownTarget,
    onPauseSignal: useCallback(
      () => signalCountdown(pauseClarificationCountdown),
      [signalCountdown],
    ),
    onResumeSignal: useCallback(
      () => signalCountdown(resumeClarificationCountdown),
      [signalCountdown],
    ),
  });
  const drafting = input.trim().length > 0;
  const countdownActive = !!countdownTarget;
  useEffect(() => {
    setComposerDrafting(drafting && countdownActive);
  }, [drafting, countdownActive, setComposerDrafting]);

  const idlePlaceholder =
    placeholder ??
    waitingPlaceholder(pendingClarification, pendingReview) ??
    (conversationActive ? 'Reply...' : 'I want to ...');
  let effectivePlaceholder = idlePlaceholder;
  if (isSending) {
    effectivePlaceholder = isPaused
      ? 'Paused — resume to keep streaming the reply...'
      : 'Add a follow-up — it sends when this reply finishes...';
  } else if (isDragActive) {
    effectivePlaceholder = 'Drop files to attach them';
  } else if (attachedWorkflow) {
    effectivePlaceholder = `Describe what "${attachedWorkflow.name}" should focus on (optional)...`;
  }

  const sendDisabled =
    menuOpen ||
    (!input.trim() && uploadedAttachments.length === 0 && !attachedWorkflow) ||
    isUploading;
  // Typing stays available mid-turn: the send action queues the prompt.
  const sendButtonVariant = isSending
    ? 'secondary'
    : sendDisabled
      ? 'ghost'
      : 'default';

  return (
    <div
      {...getRootProps()}
      className={cn(
        'relative flex flex-col gap-1 rounded-xl bg-accent p-3 transition-colors',
        'focus-within:ring-1 focus-within:ring-ring/40',
        isDragActive && 'ring-1 ring-primary/60',
      )}
    >
      {menuOpen && (
        <WorkflowSlashMenu
          options={menuOptions}
          activeIndex={activeOptionIndex}
          isLoading={isLoadingWorkflows}
          onHover={setActiveOptionIndex}
          onSelect={selectWorkflow}
        />
      )}

      {(attachedWorkflow || armedSkill) && (
        <div className='flex flex-wrap gap-1.5 px-1 pt-1'>
          {armedSkill && (
            <span className='inline-flex items-center gap-1.5 rounded-md bg-primary/10 px-2 py-1 text-xs font-medium text-primary'>
              <Package className='size-3.5' />
              <span className='max-w-56 truncate'>{armedSkill.name}</span>
              <button
                type='button'
                aria-label='Remove skill'
                className='rounded-sm hover:bg-primary/20'
                onClick={() => setArmedSkill(null)}
              >
                <X className='size-3' />
              </button>
            </span>
          )}
          {attachedWorkflow && (
            <span className='inline-flex items-center gap-1.5 rounded-md bg-primary/10 px-2 py-1 text-xs font-medium text-primary'>
              <Workflow className='size-3.5' />
              <span className='max-w-56 truncate'>{attachedWorkflow.name}</span>
              <button
                type='button'
                aria-label='Remove workflow'
                className='rounded-sm hover:bg-primary/20'
                onClick={() => setAttachedWorkflow(null)}
              >
                <X className='size-3' />
              </button>
            </span>
          )}
        </div>
      )}

      <Textarea
        ref={composerRef}
        value={input}
        onChange={(e) => {
          handleInputChange(e.target.value);
          pauseDetection.onInputChange(e.target.value);
        }}
        onKeyDown={handleKeyDown}
        placeholder={effectivePlaceholder}
        rows={1}
        aria-label='Message'
        className={cn(
          'min-h-0 resize-none border-0 bg-transparent p-3 text-base shadow-none',
          // The Textarea primitive tints itself in dark mode.
          'dark:bg-transparent',
          'placeholder:text-muted-foreground focus-visible:ring-0',
          'disabled:cursor-default disabled:opacity-100',
        )}
      />

      {attachmentsEnabled && (
        <ChatAttachments
          attachments={attachments}
          onRemove={removeAttachment}
          disabled={isSending}
        />
      )}

      <div className='flex items-end justify-between gap-2'>
        <div className='flex items-center gap-1'>
          {attachmentsEnabled && (
            <input
              ref={fileInputRef}
              type='file'
              multiple
              className='hidden'
              onChange={(e) => {
                addFiles(Array.from(e.target.files ?? []));
                e.target.value = '';
              }}
            />
          )}
          <DropdownMenu open={plusMenuOpen} onOpenChange={setPlusMenuOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                size='icon'
                variant='ghost'
                disabled={isSending}
                className='size-9 shrink-0 hover:bg-background/40'
                aria-label='Open actions menu'
              >
                <Plus className='size-4' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align='start'
              className='w-56'
              onCloseAutoFocus={(event) => {
                const action = afterMenuCloseRef.current;
                if (!action) return;
                afterMenuCloseRef.current = null;
                event.preventDefault();
                action();
              }}
            >
              {attachmentsEnabled && (
                <DropdownMenuItem
                  onSelect={() => fileInputRef.current?.click()}
                >
                  <Paperclip className='size-4' />
                  Add files
                </DropdownMenuItem>
              )}
              {workflowCommands && (
                <>
                  <DropdownMenuItem
                    onSelect={() => {
                      // An attached workflow suppresses the picker, so
                      // reopening one has to drop it first.
                      setAttachedWorkflow(null);
                      handleInputChange('/');
                      afterMenuCloseRef.current = () =>
                        composerRef.current?.focus();
                    }}
                  >
                    <Play className='size-4' />
                    Run a workflow
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() => navigate(ROUTES.WORKFLOW.ROOT)}
                  >
                    <Workflow className='size-4' />
                    Create a workflow
                  </DropdownMenuItem>
                </>
              )}
              {skillsEnabled && (
                <>
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger className='gap-2'>
                      <Package className='size-4 text-muted-foreground' />
                      Use a Skill
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent className='w-64'>
                      {isLoadingSkills && (
                        <DropdownMenuItem disabled>
                          Loading skills...
                        </DropdownMenuItem>
                      )}
                      {skillsErrored && !isLoadingSkills && (
                        <DropdownMenuItem disabled>
                          Couldn't load skills
                        </DropdownMenuItem>
                      )}
                      {!isLoadingSkills &&
                        !skillsErrored &&
                        skillOptions.length === 0 && (
                          <DropdownMenuItem disabled>
                            No skills yet
                          </DropdownMenuItem>
                        )}
                      {skillOptions.length > 0 && (
                        <div className='max-h-64 overflow-y-auto'>
                          {skillOptions.map((skill) => (
                            <DropdownMenuItem
                              key={skill.skill_id}
                              onSelect={() =>
                                setArmedSkill({
                                  skillId: skill.skill_id,
                                  name: skill.name,
                                })
                              }
                            >
                              <Package className='size-4' />
                              <span className='min-w-0 flex-1 truncate'>
                                {skill.name}
                              </span>
                              {armedSkill?.skillId === skill.skill_id && (
                                <Check className='size-4 text-primary' />
                              )}
                            </DropdownMenuItem>
                          ))}
                        </div>
                      )}
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                  <DropdownMenuItem onSelect={() => navigate(ROUTES.SKILLS)}>
                    <BookOpen className='size-4' />
                    Create a Skill
                  </DropdownMenuItem>
                </>
              )}
              {settingsEnabled && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={() => {
                      afterMenuCloseRef.current = () => setSettingsOpen(true);
                    }}
                  >
                    <span className='relative flex'>
                      <Cog className='size-4' />
                      {settingsActive && (
                        <span className='absolute -top-0.5 -right-0.5 size-1.5 rounded-full bg-blue-500' />
                      )}
                    </span>
                    Chat settings
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <Modal
            title='Chat settings'
            isDialogOpen={settingsOpen}
            setIsDialogOpen={setSettingsOpen}
            contentClassName='sm:max-w-lg'
          >
            <ChatSettingsContent
              customInstructionsEnabled={customInstructionsEnabled}
              clarificationSettingsEnabled={clarificationSettingsEnabled}
              customInstructions={customInstructions}
              onSaveCustomInstructions={setCustomInstructions}
              onDone={() => setSettingsOpen(false)}
            />
          </Modal>
        </div>

        <div className='flex min-w-0 items-center gap-2'>
          {isSending && (
            <Button
              onClick={isPaused ? resumeTurn : pauseTurn}
              size='icon'
              variant='ghost'
              className='size-9 shrink-0 hover:bg-background/40'
              aria-label={isPaused ? 'Resume streaming' : 'Pause streaming'}
            >
              {isPaused ? (
                <Play className='size-4' />
              ) : (
                <Pause className='size-4' />
              )}
            </Button>
          )}
          <Button
            onClick={isSending ? stopTurn : handleSend}
            disabled={!isSending && sendDisabled}
            size='icon'
            variant={sendButtonVariant}
            className='size-9 shrink-0'
            aria-label={isSending ? 'Stop generating' : 'Send message'}
          >
            {isSending ? (
              <Square className='size-4 fill-current' />
            ) : (
              <SendHorizontal className='size-4' />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
